<template>
    <div>
        <SkillsAutocomplete
            :model-value="modelValue"
            :rules-global-skill-label-min-length="rulesGlobalSkillLabelMinLength"
            :rules-global-skill-label-max-length="rulesGlobalSkillLabelMaxLength"
            :data-testid="dataTestid"
            :disabled="disabled"
            :disabled-label="disabledLabel"
            :placeholder="placeholder"
            :show-occurrences="showOccurences"
            :show-tag-list="showTagList"
            :invalid="invalid"
            :errors="errors"
            @skill-add="handleSkillAdd"
            @skill-remove="handleSkillRemove"
            @update:model-value="handleUpdateModelValue"
        />
    </div>
</template>
<script setup lang="ts">
    import {toRefs} from 'vue';
    import SkillsAutocomplete, {type SkillLabel} from '~/features/skills/ui/SkillsAutocomplete.vue';
    import {type Skill as SkillApi} from '~/openapi';

    export type Props = {
        modelValue: SkillLabel[] | SkillApi[];
        rulesGlobalSkillLabelMinLength: number;
        rulesGlobalSkillLabelMaxLength: number;
        dataTestid?: string;
        disabled?: boolean;
        disabledLabel?: string;
        label?: string;
        placeholder?: string;
        showLabel?: boolean;
        showOccurences?: boolean;
        showTagList?: boolean;
        invalid?: boolean;
        errors?: string[];
    };

    const props = withDefaults(defineProps<Props>(), {
        rulesGlobalSkillLabelMaxLength: 150,
        rulesGlobalSkillLabelMinLength: 1,
        dataTestid: 'profile-edition-section-skill-set-global-skills',
        disabled: false,
        showLabel: true,
        showOccurences: false,
        showTagList: false,
        invalid: false,
        errors: () => [],
    });

    const {modelValue, rulesGlobalSkillLabelMaxLength, dataTestid, disabled, label, placeholder, showOccurences, showTagList, errors} = toRefs(props);

    const emit = defineEmits<{
        add: [value: SkillApi];
        remove: [value: SkillApi];
        'update:modelValue': [value: SkillApi[]];
    }>();

    // Handlers
    function handleSkillAdd(skillToAdd: SkillLabel | SkillApi) {
        emit('add', skillToAdd as SkillApi);
    }

    function handleSkillRemove(skillToRemove: SkillLabel | SkillApi) {
        emit('remove', skillToRemove as SkillApi);
    }

    function handleUpdateModelValue(skillsToUpdate: SkillLabel[] | SkillApi[]) {
        emit('update:modelValue', skillsToUpdate as SkillApi[]);
    }
</script>
<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .profile-edition-skill-set {
        &__global-skills {
            &__label {
                font-weight: var(--joy-font-weight-normal);
                margin-bottom: var(--joy-core-spacing-2);
            }
            &__list {
                margin-top: var(--joy-core-spacing-2);
                display: flex;
                gap: var(--joy-core-spacing-1) var(--joy-core-spacing-2);
                flex-wrap: wrap;
            }

            &__option {
                &__counter {
                    @include text.getFontBySize(primary-200);
                    color: var(--joy-color-neutral-40);
                }

                &__tag-to-create {
                    font-weight: var(--joy-font-weight-bold);
                    color: var(--joy-color-neutral-70);
                }
            }

            &--disabled {
                cursor: not-allowed;
            }
        }
    }
</style>
